import { z } from 'zod';

import { Id, IsoDateTime } from '../common/primitives';

export const NotificationChannelSchema = z.enum(['EMAIL', 'SMS', 'WHATSAPP', 'IN_APP']);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

export const NotificationStatusSchema = z.enum(['QUEUED', 'SENT', 'DELIVERED', 'FAILED']);
export type NotificationStatus = z.infer<typeof NotificationStatusSchema>;

// ── Response shapes ────────────────────────────────────────────────────────

export const NotificationTemplateSchema = z.object({
  id: Id,
  key: z.string(),
  channel: NotificationChannelSchema,
  locale: z.string(),
  subject: z.string().nullable(),
  body: z.string(),
  isActive: z.boolean(),
  updatedAt: IsoDateTime,
});
export type NotificationTemplate = z.infer<typeof NotificationTemplateSchema>;

export const NotificationLogSchema = z.object({
  id: Id,
  templateKey: z.string(),
  channel: NotificationChannelSchema,
  toAddress: z.string(),
  status: NotificationStatusSchema,
  providerId: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: IsoDateTime,
  sentAt: IsoDateTime.nullable(),
});
export type NotificationLog = z.infer<typeof NotificationLogSchema>;

// ── Request shapes ─────────────────────────────────────────────────────────

export const UpsertTemplateRequest = z
  .object({
    key: z.string().trim().min(3).max(80),
    channel: NotificationChannelSchema,
    locale: z.string().trim().min(2).max(8).default('en-IN'),
    subject: z.string().trim().max(200).optional(),
    body: z.string().trim().min(2).max(8000),
    isActive: z.boolean().optional(),
  })
  .strict();
export type UpsertTemplateRequest = z.infer<typeof UpsertTemplateRequest>;

/**
 * Send a notification to a specific recipient. Caller chooses the template and channel;
 * server renders `body`/`subject` against `vars`. Audience-targeting (broadcast/segments)
 * is layered on later in the campaigns module.
 */
export const SendNotificationRequest = z
  .object({
    templateKey: z.string().trim().min(3).max(80),
    channel: NotificationChannelSchema,
    locale: z.string().trim().min(2).max(8).optional(),
    /// Channel-specific recipient: email, E.164 phone, or user id for IN_APP.
    to: z.string().trim().min(3).max(200),
    /// Substitution variables for `{{var}}` placeholders in the template body/subject.
    vars: z.record(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
  })
  .strict();
export type SendNotificationRequest = z.infer<typeof SendNotificationRequest>;

export const ListLogsQuery = z
  .object({
    templateKey: z.string().optional(),
    status: NotificationStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type ListLogsQuery = z.infer<typeof ListLogsQuery>;
